(() => {
  // src/lib/dom.js
  var $$ = (selector, scope) => Array.from((scope || document).querySelectorAll(selector));
  function onReady(fn) {
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", fn);
    else fn();
  }
  function $$within(scope, selector) {
    const found = $$(selector, scope);
    if (
      /** @type {Element} */
      scope.matches?.(selector) && !found.includes(
        /** @type {HTMLElement} */
        scope
      )
    )
      found.unshift(
        /** @type {HTMLElement} */
        scope
      );
    return found;
  }

  // src/lib/events.js
  var CLOSE_HEADER_OVERLAYS = "bb:close-header-overlays";
  var CLOSE_FILTER_DRAWERS = "bb:close-filter-drawers";
  var closeHeaderOverlays = () => document.dispatchEvent(new CustomEvent(CLOSE_HEADER_OVERLAYS));
  var closeFilterDrawers = (except = null) => document.dispatchEvent(new CustomEvent(CLOSE_FILTER_DRAWERS, { detail: { except } }));

  // src/lib/once.js
  function firstLoad(name) {
    const key = /* @__PURE__ */ Symbol.for(`bblab.${name}`);
    const registry = (
      /** @type {Record<symbol, boolean>} */
      /** @type {unknown} */
      document
    );
    if (registry[key]) return false;
    registry[key] = true;
    return true;
  }

  // src/components/filter-drawer/background-inert.js
  function inertNode(node, records) {
    const had = node.hasAttribute("inert");
    records.push({ node, had });
    if (!had) node.setAttribute("inert", "");
  }
  function inertOutside(node, drawer, records) {
    if (node === drawer) return;
    if (node.contains(drawer)) Array.from(node.children).forEach((child) => inertOutside(child, drawer, records));
    else inertNode(node, records);
  }
  function inertAround(drawer) {
    const records = [];
    ["#header", "#MainContent", "footer"].forEach((selector) => {
      const node = document.querySelector(selector);
      if (!node) return;
      if (node === drawer || node.contains(drawer)) Array.from(node.children).forEach((child) => inertOutside(child, drawer, records));
      else inertNode(node, records);
    });
    return records;
  }
  function restoreInert(records) {
    (records || []).forEach(({ node, had }) => node.toggleAttribute("inert", had));
  }

  // src/components/filter-drawer/focusables.js
  function blocksFocus(el) {
    if (el.hidden || el.hasAttribute("inert") || el.getAttribute("aria-hidden") === "true") return true;
    const style = window.getComputedStyle(el);
    return style.display === "none" || style.visibility === "hidden";
  }
  function reachable(node) {
    for (let el = node; el && el.nodeType === 1; el = el.parentElement) if (blocksFocus(el)) return false;
    return node.getClientRects().length > 0;
  }
  function tabbable(node) {
    if (node.disabled || node.getAttribute("tabindex") === "-1") return false;
    const tag = node.tagName.toLowerCase();
    if (tag === "input" && node.getAttribute("type") === "hidden") return false;
    return !(tag === "a" && !node.hasAttribute("href"));
  }
  var focusablesIn = (panel) => $$within(panel, "a[href], button, input, select, textarea, [tabindex]").filter((node) => tabbable(node) && reachable(node));

  // src/components/filter-drawer/filter-drawer.js
  var CLOSE_MS = 400;
  var BODY_CLASS = "filter-drawer-open";
  var drawers = /* @__PURE__ */ new WeakMap();
  var reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var otherOverlayOpen = () => ["search-open", "menu-open", "saved-open", "mob-nav-open"].some((name) => document.body.classList.contains(name));
  var FilterDrawer = class {
    constructor(drawer, trigger, panel) {
      const q = (selector) => drawer.querySelector(selector);
      this.drawer = drawer;
      this.trigger = trigger;
      this.panel = panel;
      this.backdrop = q("[data-filter-backdrop]");
      this.closeButton = q("[data-filter-close]");
      this.tabs = $$within(drawer, "[data-filter-tab]");
      this.tabPanels = $$within(drawer, "[data-filter-tabpanel]");
      this.timer = null;
      this.open = false;
      this.generation = 0;
      this.inert = null;
    }
    cancelTimer() {
      if (this.timer) window.clearTimeout(this.timer);
      this.timer = null;
    }
    showTab(name) {
      this.tabs.forEach((tab) => {
        const on = tab.getAttribute("data-filter-tab") === name;
        tab.setAttribute("aria-selected", String(on));
        tab.tabIndex = on ? 0 : -1;
      });
      this.tabPanels.forEach((tabPanel) => {
        tabPanel.hidden = tabPanel.getAttribute("data-filter-tabpanel") !== name;
      });
    }
    releasePage() {
      restoreInert(this.inert);
      this.inert = null;
      document.body.classList.remove(BODY_CLASS);
    }
    finishClose(generation, focusTrigger) {
      if (generation !== this.generation) return;
      this.timer = null;
      this.panel.hidden = true;
      if (this.backdrop) this.backdrop.hidden = true;
      this.panel.setAttribute("aria-hidden", "true");
      this.releasePage();
      if (focusTrigger && !otherOverlayOpen()) this.trigger.focus();
    }
    close(focusTrigger) {
      if (!this.open) return;
      this.open = false;
      this.drawer.dataset.open = "false";
      this.trigger.setAttribute("aria-expanded", "false");
      this.cancelTimer();
      const generation = ++this.generation;
      if (reducedMotion()) this.finishClose(generation, focusTrigger);
      else this.timer = window.setTimeout(() => this.finishClose(generation, focusTrigger), CLOSE_MS);
    }
    firstControl() {
      return this.closeButton || focusablesIn(this.panel)[0] || this.panel;
    }
    show() {
      this.generation += 1;
      this.cancelTimer();
      if (this.open) return;
      closeHeaderOverlays();
      closeFilterDrawers(this.drawer);
      this.open = true;
      this.panel.hidden = false;
      this.panel.setAttribute("aria-hidden", "false");
      if (this.backdrop) this.backdrop.hidden = false;
      this.trigger.setAttribute("aria-expanded", "true");
      this.inert = inertAround(this.drawer);
      document.body.classList.add(BODY_CLASS);
      void this.panel.offsetWidth;
      this.drawer.dataset.open = "true";
      this.firstControl().focus();
      window.setTimeout(() => {
        if (!this.open || otherOverlayOpen()) return;
        const again = this.firstControl();
        if (document.activeElement !== again) again.focus();
      }, 0);
    }
    /** Section unload: drop every lock immediately. */
    cleanup() {
      this.generation += 1;
      this.cancelTimer();
      this.open = false;
      this.panel.hidden = true;
      if (this.backdrop) this.backdrop.hidden = true;
      this.releasePage();
      this.drawer.dataset.open = "false";
      this.trigger.setAttribute("aria-expanded", "false");
    }
    // A GET form would send `filter.v.price.gte=` for every empty number input. Empty ones
    // are disabled only while the browser builds the query, then restored on the next tick.
    bindForm() {
      const form = this.drawer.querySelector("[data-filter-form]");
      if (!form) return;
      const numbers = () => (
        /** @type {HTMLInputElement[]} */
        $$within(form, 'input[type="number"]')
      );
      form.addEventListener("submit", (event) => {
        if (event.defaultPrevented) return;
        numbers().forEach((input) => {
          input.disabled = input.value === "";
        });
        window.setTimeout(
          () => numbers().forEach((input) => {
            input.disabled = false;
          }),
          0
        );
      });
    }
    onTabKey(event) {
      const { tabs } = this;
      const at = tabs.indexOf(event.currentTarget);
      const next = (/* @__PURE__ */ new Map([
        ["ArrowRight", (at + 1) % tabs.length],
        ["ArrowDown", (at + 1) % tabs.length],
        ["ArrowLeft", (at - 1 + tabs.length) % tabs.length],
        ["ArrowUp", (at - 1 + tabs.length) % tabs.length],
        ["Home", 0],
        ["End", tabs.length - 1]
      ])).get(event.key);
      if (next === void 0) return;
      event.preventDefault();
      this.showTab(tabs[next].getAttribute("data-filter-tab"));
      tabs[next].focus();
    }
    onPanelKey(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        this.close(true);
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusablesIn(this.panel);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !this.panel.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
    bind() {
      this.trigger.addEventListener("click", () => this.show());
      this.closeButton?.addEventListener("click", () => this.close(true));
      this.backdrop?.addEventListener("click", () => this.close(true));
      this.bindForm();
      this.tabs.forEach((tab) => {
        tab.addEventListener("click", () => this.showTab(tab.getAttribute("data-filter-tab")));
        tab.addEventListener("keydown", (event) => this.onTabKey(event));
      });
      this.panel.addEventListener("keydown", (event) => this.onPanelKey(event));
    }
  };
  function wire(drawer) {
    if (drawers.has(drawer)) return;
    const trigger = drawer.querySelector("[data-filter-open]");
    const panel = drawer.querySelector("[data-filter-panel]");
    if (!trigger || !panel) return;
    const instance = new FilterDrawer(drawer, trigger, panel);
    drawers.set(drawer, instance);
    instance.bind();
  }
  if (firstLoad("filter")) {
    const init = (scope) => $$within(scope, "[data-filter-drawer]").forEach(wire);
    document.addEventListener(CLOSE_FILTER_DRAWERS, (event) => {
      const except = event.detail && event.detail.except || null;
      $$within(document, "[data-filter-drawer]").forEach((drawer) => {
        if (drawer !== except) drawers.get(drawer)?.close(true);
      });
    });
    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      const open = drawers.get($$within(document, '[data-filter-drawer][data-open="true"]')[0]);
      if (open) {
        event.preventDefault();
        open.close(true);
      }
    });
    onReady(() => init(document));
    document.addEventListener("shopify:section:load", (event) => {
      if (event.target) init(
        /** @type {Element} */
        event.target
      );
    });
    document.addEventListener("shopify:section:unload", (event) => {
      if (event.target) $$within(
        /** @type {Element} */
        event.target,
        "[data-filter-drawer]"
      ).forEach((drawer) => drawers.get(drawer)?.cleanup());
    });
  }
})();
